/**
 * BillFormatsHelper.js
 * Comprehensive IRD-compliant bill formatting and utilities for Food & Room Bills
 */

// Format definitions and metadata
export const BILL_FORMATS = [
  {
    id: "pos_receipt",
    name: "Standard POS Thermal Receipt (Order Page Format)",
    shortName: "POS Bill (Order Format)",
    badge: "Order Page POS Format",
    badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-300",
    paper: "80mm / 58mm Thermal Roll",
    description: "Exact POS thermal receipt used in the Order Page 'Pay and Complete' flow with clean mono layout, itemized charges, and explicit tax additions.",
    icon: "receipt"
  },
  {
    id: "retail_tax_invoice",
    name: "Nepal Retail Tax Invoice (80mm / 58mm POS)",
    shortName: "Retail Tax Invoice",
    badge: "Featured IRD Format",
    badgeColor: "bg-blue-100 text-blue-800 border-blue-300",
    paper: "80mm / 58mm Thermal Roll",
    description: "Exact retail POS tax invoice format matching Nepal standards with Area, Table Name/No, Nepali Date (वि.सं.), Fiscal Year, Payment Mode Box, dual Cashier/Customer signatures, and STJ Technologies branding.",
    icon: "receipt"
  },
  {
    id: "thermal_ird",
    name: "IRD Standard Thermal (80mm / 58mm)",
    shortName: "IRD Thermal",
    badge: "Official IRD Standard",
    badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-300",
    paper: "80mm / 58mm Thermal Roll",
    description: "Official Inland Revenue Department (आन्तरिक राजस्व विभाग) bilingual tax invoice format with standard dashed borders, complete tax schedules, customer PAN, and QR verification.",
    icon: "printer"
  },
  {
    id: "modern_pos",
    name: "Modern Compact POS (80mm / 58mm)",
    shortName: "Modern POS",
    badge: "Contemporary & Fast",
    badgeColor: "bg-blue-100 text-blue-800 border-blue-300",
    paper: "80mm / 58mm Thermal Roll",
    description: "High-contrast, sleek modern styling designed for contemporary cafes, bistros, and express checkouts with clean typography and full IRD compliance.",
    icon: "receipt"
  },
  {
    id: "standard_a4_a5",
    name: "Standard IRD Tax Invoice (A4 / A5 Letterhead)",
    shortName: "IRD A4 / A5",
    badge: "Corporate Letterhead",
    badgeColor: "bg-purple-100 text-purple-800 border-purple-300",
    paper: "A4 / A5 Sheet",
    description: "Formal full-page corporate Tax Invoice (कर बिजक) with dual Seller/Buyer credential blocks, bordered itemized table, and dual signature lines.",
    icon: "file-text"
  },
  {
    id: "luxury_folio",
    name: "Executive Hotel & Fine Dining Folio (A4 / A5)",
    shortName: "Executive Folio",
    badge: "Luxury Hospitality",
    badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
    paper: "A4 / A5 Sheet",
    description: "Premium executive hotel folio with emerald/gold accents, categorizing room stay charges, dining orders, and amenities with complete IRD tax reconciliation.",
    icon: "star"
  }
];

// Number to Words Converter for Currency
export function numberToWords(amount, currencyName = "Rupees") {
  const num = Math.abs(Number(amount) || 0);
  if (num === 0) return `Zero ${currencyName} Only`;

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  function convertGroup(n) {
    let groupStr = "";
    if (n >= 100) {
      groupStr += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      groupStr += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      groupStr += ones[n] + " ";
    }
    return groupStr.trim();
  }

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  let words = "";

  // Indian/Nepali numbering system: Crores, Lakhs, Thousands, Hundreds
  let remaining = integerPart;

  const crore = Math.floor(remaining / 10000000);
  remaining %= 10000000;

  const lakh = Math.floor(remaining / 100000);
  remaining %= 100000;

  const thousand = Math.floor(remaining / 1000);
  remaining %= 1000;

  const hundreds = remaining;

  if (crore > 0) words += convertGroup(crore) + " Crore ";
  if (lakh > 0) words += convertGroup(lakh) + " Lakh ";
  if (thousand > 0) words += convertGroup(thousand) + " Thousand ";
  if (hundreds > 0) words += convertGroup(hundreds) + " ";

  words = words.trim();
  if (!words) words = "Zero";

  let result = `${words} ${currencyName}`;

  if (decimalPart > 0) {
    result += ` and ${convertGroup(decimalPart)} Paisa`;
  }

  return `${result} Only`;
}

// Nepali Fiscal Year Calculator (approx mid-July transition)
export function getNepaliFiscalYear(dateInput = new Date()) {
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return "2081/82";
    const adYear = d.getFullYear();
    const adMonth = d.getMonth() + 1; // 1-12
    const adDay = d.getDate();

    // In Nepal, Shrawan 1 falls around July 16-17.
    // Before July 16, it is BS (adYear + 56)/(adYear + 57), after July 16 it is (adYear + 57)/(adYear + 58)
    const isAfterShrawan = adMonth > 7 || (adMonth === 7 && adDay >= 16);
    const bsYear = isAfterShrawan ? adYear + 57 : adYear + 56;
    const nextBsYearShort = String((bsYear + 1) % 100).padStart(2, "0");

    return `${bsYear}/${nextBsYearShort}`;
  } catch (e) {
    return "2081/82";
  }
}

// Days in BS months for BS years 2075 to 2095, with starting AD date for Baisakh 1
const BS_CALENDAR_DATA = {
  2075: { start: "2018-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2076: { start: "2019-04-14", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30] },
  2077: { start: "2020-04-13", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31] },
  2078: { start: "2021-04-14", days: [31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30] },
  2079: { start: "2022-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2080: { start: "2023-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2081: { start: "2024-04-13", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31] },
  2082: { start: "2025-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2083: { start: "2026-04-14", days: [31, 31, 32, 31, 31, 30, 30, 29, 30, 29, 30, 31] },
  2084: { start: "2027-04-14", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31] },
  2085: { start: "2028-04-13", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31] },
  2086: { start: "2029-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2087: { start: "2030-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2088: { start: "2031-04-14", days: [31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30] },
  2089: { start: "2032-04-13", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31] },
  2090: { start: "2033-04-14", days: [31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31] },
};

// Accurately converts AD date to Bikram Sambat string e.g. "2083.06.07"
export function getNepaliDateBS(dateInput = new Date()) {
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return "2083.06.07";
    const targetTime = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const bsYears = Object.keys(BS_CALENDAR_DATA).map(Number).sort((a, b) => a - b);
    for (let i = 0; i < bsYears.length; i++) {
      const yr = bsYears[i];
      const data = BS_CALENDAR_DATA[yr];
      const [sY, sM, sD] = data.start.split("-").map(Number);
      const startTime = new Date(sY, sM - 1, sD).getTime();
      let nextStartTime = (i + 1 < bsYears.length) ? new Date(BS_CALENDAR_DATA[bsYears[i + 1]].start).getTime() : startTime + 365 * 86400000;
      if (targetTime >= startTime && targetTime < nextStartTime) {
        let diffDays = Math.round((targetTime - startTime) / 86400000);
        let m = 0;
        for (; m < 12; m++) {
          if (diffDays < data.days[m]) break;
          diffDays -= data.days[m];
        }
        return `${yr}.${String(m + 1).padStart(2, "0")}.${String(diffDays + 1).padStart(2, "0")}`;
      }
    }
    const adY = d.getFullYear();
    const bsY = (d.getMonth() + 1 > 3 || (d.getMonth() + 1 === 4 && d.getDate() >= 14)) ? adY + 57 : adY + 56;
    return `${bsY}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
  } catch (e) {
    return "2083.06.07";
  }
}

// Formats datetime string to "MM/DD/YYYY hh:mm:ss A" (e.g. "09/23/2026 03:42:44 PM")
export function formatReceiptDateTime(dateInput = new Date()) {
  if (!dateInput) dateInput = new Date();
  let d = new Date(dateInput);
  if (isNaN(d.getTime()) && typeof dateInput === "string") {
    d = new Date(dateInput.replace(" ", "T"));
  }
  if (isNaN(d.getTime())) return String(dateInput);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hh = String(hours).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${hh}:${minutes}:${seconds} ${ampm}`;
}

// Generate Room Bill HTML supporting all 4 IRD formats
export function generateRoomBillHTML(folioData, requestedFormat, printSettings = {}, storeSettings = {}, isEstimate = false, autoPrint = true) {
  if (!folioData) return "";

  const {
    booking = {},
    nights = 1,
    roomTotal = 0,
    extraCharges = [],
    extraTotal = 0,
    orders = [],
    orderTotal = 0,
    totalCharges = 0,
    payments = 0,
    balance = 0,
    store = {}
  } = folioData;

  const activeFormat = requestedFormat || printSettings?.bill_format || localStorage.getItem("bill_format") || "thermal_ird";
  const hotelName = store?.store_name || storeSettings?.store_name || "Hotel RestroPro";
  const hotelAddress = store?.address || storeSettings?.address || "";
  const hotelPhone = store?.phone || storeSettings?.phone || "";
  const hotelEmail = store?.email || storeSettings?.email || "";
  const panNo = store?.pan_no || store?.panNo || storeSettings?.pan_no || storeSettings?.panNo || "";
  const isVat = Boolean(store?.is_vat_registered || storeSettings?.is_vat_registered);
  const currency = store?.currency || storeSettings?.currency || "Rs.";
  const fiscalYear = getNepaliFiscalYear(booking?.check_in || new Date());
  const docInvoiceNo = isEstimate 
    ? (folioData?.estimateNo || `EST-R${booking?.id || "000"}`) 
    : (folioData?.invoiceNo || folioData?.invoice_no || booking?.invoice_no || `INV-R${booking?.id || "000"}`);
  const invoiceNo = docInvoiceNo;
  const paymentMethod = folioData?.paymentType || folioData?.payment_method || booking?.payment_method || "Cash";
  const transactionDate = new Date().toLocaleString();
  const checkInDate = booking?.check_in ? new Date(booking.check_in).toLocaleString() : "N/A";
  const checkOutDate = booking?.check_out ? new Date(booking.check_out).toLocaleString() : new Date().toLocaleString();
  const guestName = booking?.guest_name || "Guest";
  const guestPhone = booking?.guest_phone || "N/A";
  const guestPan = booking?.guest_pan || booking?.pan_no || "N/A";
  const roomNumber = booking?.room_number || "N/A";
  const roomType = booking?.room_type_name || "Standard";
  const checkoutNote = folioData?.note || folioData?.notes || booking?.notes || "";

  const discountAmt = Number(folioData?.discount || folioData?.discountAmount || 0);
  const roundOffAmt = Number(folioData?.roundOff || folioData?.roundOffAmount || 0);
  const serviceChargeAmt = Number(folioData?.serviceCharge || folioData?.serviceChargeAmount || 0);
  const subTotal = Number(folioData?.subTotal || folioData?.itemsTotal || (Number(roomTotal || 0) + Number(extraTotal || 0) + Number(orderTotal || 0)));
  const taxableAmt = folioData?.taxableAmount !== undefined && folioData?.taxableAmount !== null
    ? Number(folioData.taxableAmount)
    : Math.max(0, subTotal - discountAmt + serviceChargeAmt);

  let vatAmt = 0;
  if (folioData?.taxAmount !== undefined && folioData?.taxAmount !== null) {
    vatAmt = Number(folioData.taxAmount);
  } else if (folioData?.taxTotal !== undefined && folioData?.taxTotal !== null) {
    vatAmt = Number(folioData.taxTotal);
  } else if (Array.isArray(folioData?.taxes) && folioData.taxes.length > 0) {
    let calc = 0;
    folioData.taxes.forEach((t) => {
      const r = Number(t.rate || 0);
      if (t.type === "exclusive") calc += (taxableAmt * r) / 100;
      else if (t.type === "inclusive") calc += taxableAmt - (taxableAmt * (100 / (100 + r)));
    });
    vatAmt = Math.round(calc * 100) / 100;
  } else if (isVat) {
    vatAmt = Number((taxableAmt * 0.13).toFixed(2));
  }

  let grandTotal = 0;
  if (folioData?.totalAmount !== undefined && folioData?.totalAmount !== null) {
    grandTotal = Number(folioData.totalAmount);
  } else if (folioData?.payableTotal !== undefined && folioData?.payableTotal !== null) {
    grandTotal = Number(folioData.payableTotal);
  } else {
    grandTotal = Math.max(0, Number((taxableAmt + vatAmt + roundOffAmt).toFixed(2)));
  }

  if (vatAmt === 0 && grandTotal > (taxableAmt + roundOffAmt)) {
    vatAmt = Number((grandTotal - taxableAmt - roundOffAmt).toFixed(2));
  }

  const hasTax = isVat || vatAmt > 0;

  const advancePayment = Number(folioData?.advancePayment !== undefined ? folioData.advancePayment : (payments || 0));
  let totalPaid;
  if (folioData?.paidAmount !== undefined && folioData?.paidAmount !== null) {
    totalPaid = Number(folioData.paidAmount);
  } else if (folioData?.checkoutPaidAmount !== undefined && folioData?.checkoutPaidAmount !== null) {
    totalPaid = advancePayment + Number(folioData.checkoutPaidAmount);
  } else {
    totalPaid = advancePayment;
  }

  const amountPaidNow = folioData?.amountPaidNow !== undefined 
    ? Number(folioData.amountPaidNow) 
    : Math.max(0, totalPaid - advancePayment);

  const dueAmount = folioData?.dueAmount !== undefined 
    ? Number(folioData.dueAmount) 
    : Math.max(0, grandTotal - totalPaid);

  const paidAmount = totalPaid;
  const amountInWords = numberToWords(grandTotal, "Rupees");

  // Prepare items
  const items = [
    {
      sn: 1,
      title: `Room Stay: Room ${roomNumber} (${roomType})`,
      subtitle: `${nights} Night(s) @ ${currency} ${Number(booking?.room_rate || 0).toFixed(2)}`,
      qty: nights,
      rate: Number(booking?.room_rate || 0).toFixed(2),
      total: Number(roomTotal || 0).toFixed(2)
    }
  ];

  let snCounter = 2;
  if (extraCharges && extraCharges.length > 0) {
    extraCharges.forEach((ec) => {
      items.push({
        sn: snCounter++,
        title: `Service Charge: ${ec.title || "Extra Service"}`,
        subtitle: "Amenity / Service",
        qty: 1,
        rate: Number(ec.amount || 0).toFixed(2),
        total: Number(ec.amount || 0).toFixed(2)
      });
    });
  }

  if (orders && orders.length > 0) {
    const consolidatedMap = {};
    orders.forEach((ord) => {
      if (ord.items && ord.items.length > 0) {
        ord.items.forEach((i) => {
          if (i.status === 'cancelled') return;
          const itemName = i.variant_title ? `${i.item_name} (${i.variant_title})` : (i.item_name || "Restaurant Item");
          const rate = Number(i.price || i.catalog_price || i.menu_item_price || i.rate || 0);
          const qty = Number(i.quantity || 1);
          const key = `${i.item_id || ''}_${i.variant_id || ''}_${itemName}_${rate.toFixed(2)}`;

          if (!consolidatedMap[key]) {
            consolidatedMap[key] = {
              title: itemName,
              subtitle: "",
              qty: 0,
              rate: rate.toFixed(2),
              total: 0
            };
          }
          consolidatedMap[key].qty += qty;
          consolidatedMap[key].total += rate * qty;
        });
      } else {
        const orderTot = Number(ord.order_total || 0);
        if (orderTot > 0) {
          const key = `order_${ord.id}`;
          consolidatedMap[key] = {
            title: `Restaurant: Order #${ord.id}`,
            subtitle: "",
            qty: 1,
            rate: orderTot.toFixed(2),
            total: orderTot
          };
        }
      }
    });

    Object.values(consolidatedMap).forEach((ci) => {
      items.push({
        sn: snCounter++,
        title: ci.title,
        subtitle: ci.subtitle,
        qty: ci.qty,
        rate: ci.rate,
        total: Number(ci.total).toFixed(2)
      });
    });
  }

  // Common QR Code value
  const qrString = `Store:${hotelName}|PAN:${panNo}|Inv:${invoiceNo}|Total:${subTotal}|Date:${transactionDate}`;

  // -------------------------------------------------------------
  // FORMAT: POS RECEIPT (ORDER PAGE EXACT FORMAT)
  // -------------------------------------------------------------
  if (activeFormat === "pos_receipt") {
    const is58 = Number(String(printSettings?.page_format || 80).replace(/,/g, "")) <= 58;
    const paperWidthCss = is58 ? "48mm" : "72mm";
    const headerText = printSettings?.header || "";
    const footerText = printSettings?.footer || "Thank You! Visit Again";

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate Bill' : 'Tax Invoice'} - ${invoiceNo}</title>
  <style>
    @page { 
      size: auto; 
      margin: 2mm 3mm !important; 
    }
    * { 
      box-sizing: border-box; 
    }
    html, body { 
      font-family: 'Courier New', Courier, monospace; 
      width: 100% !important;
      margin: 0 !important; 
      padding: 0 !important; 
      color: #000; 
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .receipt-wrapper {
      width: 100% !important;
      max-width: ${paperWidthCss} !important;
      margin: 0 auto !important;
      padding: ${is58 ? '2mm 3mm' : '3mm 4mm'} !important;
      box-sizing: border-box !important;
    }
    .center { text-align: center; }
    .bold { font-weight: 900; }
    .store-name { font-size: ${is58 ? '1.1rem' : '1.35rem'}; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; }
    .sub-text { font-size: ${is58 ? '0.8rem' : '0.9rem'}; line-height: 1.2; }
    .divider { border-bottom: 1px dashed #000; margin: 4px 0; }
    .double-divider { border-bottom: 2px solid #000; margin: 5px 0; }
    .banner { border: 2px solid #000; padding: 2px 4px; margin: 4px 0; font-weight: 900; text-align: center; }
    table.items { width: 100%; border-collapse: collapse; font-size: ${is58 ? '0.75rem' : '0.85rem'}; }
    table.items th { border-bottom: 1px solid #000; padding: 2px 0; text-align: left; }
    table.items td { padding: 2px 0; vertical-align: top; }
    .text-right { text-align: right; }
    .summary-row { display: flex; justify-content: space-between; font-size: ${is58 ? '0.8rem' : '0.9rem'}; padding: 1px 0; }
    .grand-total { display: flex; justify-content: space-between; font-size: 1.1rem; font-weight: 900; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; padding: 3px 0; margin: 4px 0; }
    .cutter-space { height: 8mm; clear: both; }
  </style>
</head>
<body>
  <div class="receipt-wrapper">
    <div class="center">
      <div class="store-name">${hotelName}</div>
      ${hotelAddress ? `<div class="sub-text">${hotelAddress}</div>` : ''}
      ${hotelPhone ? `<div class="sub-text">Tel: ${hotelPhone}</div>` : ''}
      ${panNo ? `<div class="sub-text bold">${hasTax ? 'VAT' : 'PAN'}: ${panNo}</div>` : ''}
      ${headerText ? `<div class="sub-text" style="margin-top:2px;">${headerText}</div>` : ''}
    </div>

    ${isEstimate ? `
      <div class="banner">
        <div style="font-size: 0.95rem;">PROFORMA ESTIMATE</div>
        <div style="font-size: 0.72rem;">(अनुमानित बिल - NOT A TAX INVOICE)</div>
      </div>
    ` : `
      <div class="banner">
        <div style="font-size: 0.95rem;">${hasTax ? 'TAX INVOICE' : 'INVOICE'}</div>
        <div style="font-size: 0.72rem;">(कर बिजक)</div>
      </div>
    `}

    <div class="sub-text" style="margin-top: 4px;">
      ${invoiceNo ? `<div><b>Bill No:</b> ${invoiceNo}</div>` : ''}
      <div><b>Room:</b> Room ${roomNumber} (${roomType})</div>
      ${guestName && guestName !== 'Guest' ? `<div><b>Guest:</b> ${guestName}</div>` : ''}
      ${guestPan && guestPan !== 'N/A' ? `<div><b>Guest PAN:</b> ${guestPan}</div>` : ''}
      <div><b>Date:</b> ${transactionDate}</div>
      <div><b>Fiscal Year:</b> ${fiscalYear}</div>
    </div>

    <div class="divider"></div>

    <table class="items">
      <thead>
        <tr>
          <th style="width: 15%;">Qty</th>
          <th style="width: 55%;">Item</th>
          <th style="width: 30%; text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${items.map(it => `
          <tr>
            <td>${it.qty}</td>
            <td>
              <b>${it.title}</b>
              ${it.subtitle ? `<div style="font-size: 0.7rem; color: #444;">${it.subtitle}</div>` : ''}
            </td>
            <td class="text-right">${currency} ${Number(it.total).toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="divider"></div>

    <div class="summary-row"><span>Subtotal:</span><span>${currency} ${Number(subTotal).toFixed(2)}</span></div>
    ${discountAmt > 0 ? `<div class="summary-row"><span>Discount:</span><span>-${currency} ${Number(discountAmt).toFixed(2)}</span></div>` : ''}
    ${serviceChargeAmt > 0 ? `<div class="summary-row"><span>Service Chg:</span><span>+${currency} ${Number(serviceChargeAmt).toFixed(2)}</span></div>` : ''}
    <div class="summary-row"><span>Taxable Amount:</span><span>${currency} ${Number(taxableAmt).toFixed(2)}</span></div>
    ${vatAmt > 0 ? `<div class="summary-row"><span>13% VAT:</span><span>+${currency} ${Number(vatAmt).toFixed(2)}</span></div>` : ''}
    ${roundOffAmt !== 0 ? `<div class="summary-row"><span>Round Off:</span><span>${roundOffAmt > 0 ? `+` : ``}${currency} ${Number(roundOffAmt).toFixed(2)}</span></div>` : ''}

    <div class="grand-total">
      <span>${isEstimate ? 'EST. TOTAL:' : 'NET TOTAL:'}</span>
      <span>${currency} ${Number(grandTotal).toFixed(2)}</span>
    </div>

    ${!isEstimate && paymentMethod ? `<div class="summary-row bold"><span>Payment Mode:</span><span>${paymentMethod}</span></div>` : ''}
    ${advancePayment > 0 ? `<div class="summary-row"><span>Advance Paid:</span><span>-${currency} ${Number(advancePayment).toFixed(2)}</span></div>` : ''}
    ${amountPaidNow > 0 ? `<div class="summary-row"><span>Paid at Checkout:</span><span>-${currency} ${Number(amountPaidNow).toFixed(2)}</span></div>` : ''}
    <div class="summary-row bold"><span>Total Paid:</span><span>${currency} ${Number(totalPaid).toFixed(2)}</span></div>
    ${dueAmount > 0 ? `<div class="summary-row bold" style="color:#d9534f;"><span>Due Balance:</span><span>${currency} ${Number(dueAmount).toFixed(2)}</span></div>` : ''}
    ${checkoutNote ? `<div style="margin-top: 4px; padding: 2px 4px; font-size: 0.72rem; border-left: 2px solid #000; font-style: italic; word-break: break-word;"><b>Note:</b> ${checkoutNote}</div>` : ''}

    <div class="double-divider"></div>

    <div class="center sub-text" style="margin-top: 4px;">
      <div>${footerText}</div>
      <div style="font-size: 0.7rem; margin-top: 2px;">RestroPro POS • eHospitality</div>
    </div>

    <div class="cutter-space"></div>
  </div>
  ${autoPrint ? `
  <script>
    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
  </script>
  ` : ""}
</body>
</html>`;
  }

  // -------------------------------------------------------------
  // FORMAT: RETAIL TAX INVOICE (SMTECH / POS 80mm EXACT REPLICA)
  // -------------------------------------------------------------
  if (activeFormat === "retail_tax_invoice") {
    const formattedDt = formatReceiptDateTime(new Date());
    const bsDate = getNepaliDateBS(new Date());
    const wordsOnly = amountInWords.replace(/Rupees/gi, "").trim();
    const is58 = Number(String(printSettings?.page_format || 80).replace(/,/g, "")) <= 58;
    const discountPercent = subTotal > 0 && discountAmt > 0 ? Math.round((discountAmt / subTotal) * 100) : 0;

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate Bill' : 'Tax Invoice'} - ${invoiceNo}</title>
  <style>
    @page { size: ${is58 ? '58mm' : '80mm'} auto; margin: 0; }
    * { box-sizing: border-box; }
    body {
      font-family: 'Consolas', 'Courier New', Courier, Monaco, monospace;
      width: ${is58 ? '48mm' : '72mm'};
      max-width: ${is58 ? '48mm' : '72mm'};
      margin: 0 auto;
      padding: ${is58 ? '2mm 1mm' : '3mm 2mm'};
      font-size: ${is58 ? '9.5px' : '11px'};
      line-height: ${is58 ? '1.25' : '1.35'};
      color: #000;
      background: #fff;
      -webkit-font-smoothing: antialiased;
      overflow: hidden;
    }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .bold { font-weight: bold; }
    .store-name { font-size: ${is58 ? '14px' : '17px'}; font-weight: 900; line-height: 1.15; }
    .sub-header { font-size: ${is58 ? '10px' : '12px'}; font-weight: 500; margin-top: 1px; }
    .doc-title { font-size: ${is58 ? '11px' : '13px'}; font-weight: 700; margin-top: 3px; text-transform: uppercase; }
    .divider { border-top: 1px solid #000; margin: 4px 0; }
    table { width: 100%; border-collapse: collapse; margin: 2px 0; table-layout: fixed; }
    th { border-bottom: 1px solid #000; padding: 2px 0; font-size: ${is58 ? '9px' : '10.5px'}; font-weight: 700; }
    td { padding: 1.5px 0; font-size: ${is58 ? '9px' : '10.5px'}; vertical-align: top; }
  </style>
</head>
<body>
  <div class="text-center">
    <div class="store-name">${hotelName}</div>
    <div class="sub-header">${hotelAddress}</div>
    ${hotelPhone ? `<div class="sub-header">${hotelPhone}</div>` : ""}
    <div class="doc-title">${isEstimate ? 'Estimate Bill' : 'Tax Invoice'}</div>
  </div>

  <div style="margin-top: 6px; font-size: ${is58 ? '9px' : '10.5px'}; line-height: 1.3;">
    <div style="display: flex; justify-content: space-between;">
      <span>${panNo ? `PAN: ${panNo}` : ""}</span>
      <span>${guestPan && guestPan !== "N/A" ? `Buyer PAN: ${guestPan}` : ""}</span>
    </div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Bill No:</span><span>${invoiceNo}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Area:</span><span>ROOMS</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Room No:</span><span>${roomNumber}</span></div>
    ${guestName && guestName !== 'Guest' ? `<div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Guest:</span><span>${guestName}</span></div>` : ''}
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Date:</span><span>${formattedDt}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Miti:</span><span>${bsDate}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Fiscal Year:</span><span>${fiscalYear}</span></div>
  </div>

  <div class="divider"></div>

  <table>
    <thead>
      <tr>
        ${is58 ? `
        <th style="text-align: left; width: 46%;">Item</th>
        <th style="text-align: right; width: 16%;">QTY</th>
        <th style="text-align: right; width: 18%;">Price</th>
        <th style="text-align: right; width: 20%;">Total</th>
        ` : `
        <th style="text-align: left; width: 40%;">Item</th>
        <th style="text-align: center; width: 12%;">HSCode</th>
        <th style="text-align: right; width: 14%;">QTY</th>
        <th style="text-align: right; width: 16%;">Price</th>
        <th style="text-align: right; width: 18%;">Total</th>
        `}
      </tr>
    </thead>
    <tbody>
      ${items.map(it => `
        <tr>
          <td style="text-align: left; word-break: break-word;">${it.title}</td>
          ${!is58 ? `<td style="text-align: center;">0</td>` : ''}
          <td style="text-align: right; white-space: nowrap;">${Number(it.qty) % 1 === 0 ? Number(it.qty).toFixed(0) : Number(it.qty).toFixed(2)}</td>
          <td style="text-align: right; white-space: nowrap;">${Number(it.rate).toFixed(2)}</td>
          <td style="text-align: right; white-space: nowrap;">${Number(it.total).toFixed(2)}</td>
        </tr>
      `).join("")}
    </tbody>
  </table>

  <div class="divider"></div>

  ${is58 ? `
  <div style="margin: 3px 0;">
    <div style="font-size: 9.5px; line-height: 1.35; margin-bottom: 4px;">
      <div style="display: flex; justify-content: space-between; padding-right: 1px;">
        <span>Sub-Total :</span>
        <span>${Number(subTotal).toFixed(2)}</span>
      </div>
      ${discountAmt > 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 1px; color: #b91c1c;">
        <span>Discount ${discountPercent > 0 ? `(${discountPercent}%)` : ''} :</span>
        <span>-${Number(discountAmt).toFixed(2)}</span>
      </div>` : ""}
      ${serviceChargeAmt > 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 1px;">
        <span>Service Charge :</span>
        <span>+${Number(serviceChargeAmt).toFixed(2)}</span>
      </div>` : ""}
      <div style="display: flex; justify-content: space-between; padding-right: 1px; font-weight: 600;">
        <span>${vatAmt > 0 ? 'Taxable AMT :' : 'NonTaxable AMT :'}</span>
        <span>${Number(taxableAmt).toFixed(2)}</span>
      </div>
      ${vatAmt > 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 1px;">
        <span>13% VAT :</span>
        <span>+${Number(vatAmt).toFixed(2)}</span>
      </div>` : ""}
      ${roundOffAmt !== 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 1px;">
        <span>Round Off :</span>
        <span>${roundOffAmt > 0 ? `+${Number(roundOffAmt).toFixed(2)}` : Number(roundOffAmt).toFixed(2)}</span>
      </div>` : ""}
      <div style="display: flex; justify-content: space-between; padding-right: 1px; font-weight: 700;">
        <span>Total :</span>
        <span>${Number(grandTotal).toFixed(2)}</span>
      </div>
    </div>
    <div style="border: 1px solid #000; padding: 2.5px 4px; font-size: 9px;">
      <span style="font-weight: 700;">Payment Mode: </span>
      <span>${paymentMethod} (${currency} ${Number(totalPaid).toFixed(2)})</span>
      ${dueAmount > 0 ? `<span style="color: #b91c1c; font-weight: 700;"> | Due: ${currency} ${Number(dueAmount).toFixed(2)}</span>` : ""}
    </div>
  </div>
  ` : `
  <div style="display: flex; justify-content: space-between; align-items: flex-start; margin: 4px 0;">
    <div style="border: 1px solid #000; padding: 4px 5px; width: 38%; min-height: 50px; font-size: 10.5px;">
      <div style="font-weight: 700; border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 2px;">Mode Of Payment</div>
      <div style="font-weight: 600;">${paymentMethod}</div>
      <div style="font-weight: 600;">${currency} ${Number(totalPaid).toFixed(2)}</div>
      ${dueAmount > 0 ? `<div style="font-weight: 700; color: #b91c1c; font-size: 9.5px; margin-top: 2px;">Due: ${currency} ${Number(dueAmount).toFixed(2)}</div>` : ""}
    </div>

    <div style="width: 60%; font-size: 10.5px; line-height: 1.4;">
      <div style="display: flex; justify-content: space-between; padding-right: 2px;">
        <span>Sub-Total :</span>
        <span>${Number(subTotal).toFixed(2)}</span>
      </div>
      ${discountAmt > 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 2px; color: #b91c1c;">
        <span>Discount ${discountPercent > 0 ? `(${discountPercent}%)` : ''} :</span>
        <span>-${Number(discountAmt).toFixed(2)}</span>
      </div>` : ""}
      ${serviceChargeAmt > 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 2px;">
        <span>Service Charge :</span>
        <span>+${Number(serviceChargeAmt).toFixed(2)}</span>
      </div>` : ""}
      <div style="display: flex; justify-content: space-between; padding-right: 2px; font-weight: 600;">
        <span>${vatAmt > 0 ? 'Taxable AMT :' : 'NonTaxable AMT :'}</span>
        <span>${Number(taxableAmt).toFixed(2)}</span>
      </div>
      ${vatAmt > 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 2px;">
        <span>13% VAT :</span>
        <span>+${Number(vatAmt).toFixed(2)}</span>
      </div>` : ""}
      ${roundOffAmt !== 0 ? `
      <div style="display: flex; justify-content: space-between; padding-right: 2px;">
        <span>Round Off :</span>
        <span>${roundOffAmt > 0 ? `+${Number(roundOffAmt).toFixed(2)}` : Number(roundOffAmt).toFixed(2)}</span>
      </div>` : ""}
      <div style="display: flex; justify-content: space-between; padding-right: 2px; font-weight: 700;">
        <span>Total :</span>
        <span>${Number(grandTotal).toFixed(2)}</span>
      </div>
    </div>
  </div>
  `}

  <div class="divider"></div>

  <div style="display: flex; justify-content: space-between; align-items: center; padding: ${is58 ? '2px 0' : '3px 0'}; padding-right: 2px;">
    <span style="font-size: ${is58 ? '13px' : '15px'}; font-weight: 900;">Grand Total:</span>
    <span style="font-size: ${is58 ? '13px' : '15px'}; font-weight: 900;">${Number(grandTotal).toFixed(2)}</span>
  </div>

  <div class="divider"></div>

  <div style="font-size: ${is58 ? '9px' : '10.5px'}; margin-top: 3px; line-height: 1.3;">
    <div style="word-break: break-word;">In Words: ${wordsOnly}</div>
    ${advancePayment > 0 ? `
    <div style="display: flex; justify-content: space-between; margin-top: 3px;">
      <span>Advance Received :</span>
      <span>${currency} ${Number(advancePayment).toFixed(2)}</span>
    </div>` : ""}
    ${amountPaidNow > 0 ? `
    <div style="display: flex; justify-content: space-between; margin-top: 2px;">
      <span>Paid at Checkout (${paymentMethod}) :</span>
      <span>${currency} ${Number(amountPaidNow).toFixed(2)}</span>
    </div>` : ""}
    <div style="display: flex; justify-content: space-between; margin-top: 3px; font-weight: 700;">
      <span>Total Received AMT :</span>
      <span>${currency} ${Number(totalPaid).toFixed(2)}</span>
    </div>
    ${dueAmount > 0 ? `
    <div style="display: flex; justify-content: space-between; font-weight: 900; color: #b91c1c; margin-top: 2px;">
      <span>Balance Due :</span>
      <span>${currency} ${Number(dueAmount).toFixed(2)}</span>
    </div>` : `
    <div style="display: flex; justify-content: space-between; font-weight: 700; color: #047857; margin-top: 2px;">
      <span>Payment Status :</span>
      <span>FULLY SETTLED</span>
    </div>`}
    ${checkoutNote ? `
    <div style="margin-top: 4px; padding: 2px 4px; border: 1px dashed #000; font-size: ${is58 ? '8.5px' : '9.5px'}; word-break: break-word;">
      <b>Note / Remarks:</b> ${checkoutNote}
    </div>` : ""}
  </div>

  <div class="text-center" style="font-size: ${is58 ? '11px' : '13px'}; font-weight: 700; margin: 8px 0 10px 0;">
    Thank You.
  </div>

  <div style="display: flex; justify-content: space-between; font-size: ${is58 ? '9px' : '10px'}; margin-top: 10px; padding: 0 2px;">
    <div>
      <div style="width: ${is58 ? '60px' : '80px'}; border-top: 1px solid #000; margin-bottom: 2px;"></div>
      <div>Cashier</div>
    </div>
    <div style="text-align: right;">
      <div style="width: ${is58 ? '60px' : '80px'}; border-top: 1px solid #000; margin-bottom: 2px; margin-left: auto;"></div>
      <div>Customer</div>
    </div>
  </div>

  <div class="text-center" style="font-size: ${is58 ? '8.5px' : '9.5px'}; margin-top: 10px; color: #333; line-height: 1.25;">
    <div>Powered By : STJ Technologies</div>
    <div>www.ehospitalitynepal.com</div>
  </div>

  ${autoPrint ? `
  <script>
    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
  </script>
  ` : ""}
</body>
</html>`;
  }

  // -------------------------------------------------------------
  // FORMAT 1: THERMAL IRD (80mm / 58mm)
  // -------------------------------------------------------------
  if (activeFormat === "thermal_ird") {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate Bill' : 'Tax Invoice'} - ${invoiceNo}</title>
  <style>
    @page { size: 80mm auto; margin: 2mm 3mm; }
    body {
      font-family: 'Courier New', Courier, monospace;
      width: 72mm;
      margin: 0 auto;
      padding: 3mm 3mm;
      font-size: 11px;
      color: #000;
      line-height: 1.35;
    }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .bold { font-weight: bold; }
    .title { font-size: 14px; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; }
    .tax-banner { font-size: 13px; font-weight: 900; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 4px 0; margin: 5px 0; text-align: center; }
    .divider { border-top: 1px dashed #000; margin: 6px 0; }
    .double-divider { border-top: 2px solid #000; margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; margin: 4px 0; }
    th { border-bottom: 1px dashed #000; padding: 3px 0; font-size: 10px; text-align: left; }
    td { padding: 3px 0; font-size: 10px; vertical-align: top; }
    .total-row { font-size: 11px; font-weight: bold; }
    .grand-total { font-size: 13px; font-weight: 900; }
    .footer { font-size: 9px; text-align: center; margin-top: 8px; }
  </style>
</head>
<body>
  <div class="text-center">
    <div class="title">${hotelName}</div>
    <div>${hotelAddress}</div>
    ${hotelPhone ? `<div>Tel: ${hotelPhone}</div>` : ""}
    ${panNo ? `<div class="bold">${isVat ? "VAT" : "PAN"} No: ${panNo}</div>` : ""}
    ${isEstimate ? `
      <div class="tax-banner" style="background:#fef3c7; border: 1px dashed #b45309; color: #92400e;">ESTIMATE BILL / अनुमानित बिल</div>
      <div style="font-weight: bold; font-size: 10px; margin: 2px 0; color: #b45309;">
        *** THIS IS NOT A TAX INVOICE ***<br/>
        *** यो कर बिजक होइन ***
      </div>
    ` : `
      <div class="tax-banner">${isVat ? "TAX INVOICE / कर बिजक" : "ABBREVIATED TAX INVOICE / कर बिजक"}</div>
    `}
  </div>

  <div>
    <div><b>${isEstimate ? "Estimate Ref:" : "Invoice No:"}</b> ${invoiceNo}</div>
    <div><b>Fiscal Year (आ.व.):</b> ${fiscalYear}</div>
    <div><b>Date/Time:</b> ${transactionDate}</div>
    <div><b>Room:</b> Room ${roomNumber} (${roomType})</div>
    <div><b>Nights:</b> ${nights} (${checkInDate} to ${checkOutDate})</div>
  </div>

  <div class="divider"></div>

  <div>
    <div><b>Buyer's Name:</b> ${guestName}</div>
    <div><b>Buyer's Phone:</b> ${guestPhone}</div>
    <div><b>Buyer's PAN (प्यान नं.):</b> ${guestPan}</div>
  </div>

  <div class="divider"></div>

  <table>
    <thead>
      <tr>
        <th style="width: 8%;">SN</th>
        <th style="width: 52%;">Particulars</th>
        <th style="width: 15%; text-align: center;">Qty</th>
        <th style="width: 25%; text-align: right;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${items.map(it => `
        <tr>
          <td>${it.sn}</td>
          <td>
            <b>${it.title}</b><br/>
            <span style="font-size: 9px; color: #333;">${it.subtitle}</span>
          </td>
          <td style="text-align: center;">${it.qty}</td>
          <td style="text-align: right;">${it.total}</td>
        </tr>
      `).join("")}
    </tbody>
  </table>

  <div class="divider"></div>

  <table>
    <tr><td>Total Room Charges:</td><td class="text-right">${currency} ${Number(roomTotal).toFixed(2)}</td></tr>
    ${extraTotal > 0 ? `<tr><td>Service & Amenities:</td><td class="text-right">${currency} ${Number(extraTotal).toFixed(2)}</td></tr>` : ""}
    ${orderTotal > 0 ? `<tr><td>Restaurant Orders:</td><td class="text-right">${currency} ${Number(orderTotal).toFixed(2)}</td></tr>` : ""}
    <tr class="total-row"><td>Gross Total (जम्मा रकम):</td><td class="text-right">${currency} ${subTotal.toFixed(2)}</td></tr>
    ${discountAmt > 0 ? `<tr><td>Discount (छुट):</td><td class="text-right">- ${currency} ${discountAmt.toFixed(2)}</td></tr>` : ""}
    ${serviceChargeAmt > 0 ? `<tr><td>Service Charge:</td><td class="text-right">+ ${currency} ${serviceChargeAmt.toFixed(2)}</td></tr>` : ""}
    ${roundOffAmt !== 0 ? `<tr><td>Round Off (राउण्ड अफ):</td><td class="text-right">${roundOffAmt > 0 ? `+ ` : `- `}${currency} ${Math.abs(roundOffAmt).toFixed(2)}</td></tr>` : ""}
    <tr class="total-row"><td>Taxable Amount (करयोग्य रकम):</td><td class="text-right">${currency} ${Number(taxableAmt).toFixed(2)}</td></tr>
    ${vatAmt > 0 ? `<tr><td>13% VAT (मूल्य अभिवृद्धि कर):</td><td class="text-right">+ ${currency} ${Number(vatAmt).toFixed(2)}</td></tr>` : ""}
    <tr class="grand-total double-divider"><td>Net Payable (कुल जम्मा):</td><td class="text-right">${currency} ${Number(grandTotal).toFixed(2)}</td></tr>
    ${advancePayment > 0 ? `<tr><td>Advance Deposit (अग्रिम):</td><td class="text-right">- ${currency} ${advancePayment.toFixed(2)}</td></tr>` : ""}
    ${amountPaidNow > 0 ? `<tr><td>Paid at Checkout (${paymentMethod}):</td><td class="text-right">- ${currency} ${amountPaidNow.toFixed(2)}</td></tr>` : ""}
    <tr class="total-row"><td>Total Paid (जम्मा भुक्तानी):</td><td class="text-right">${currency} ${totalPaid.toFixed(2)}</td></tr>
    <tr class="total-row" style="font-size: 12px; ${dueAmount > 0 ? 'color: #b91c1c;' : 'color: #047857;'}"><td>Balance Due (बाँकी रकम):</td><td class="text-right">${currency} ${dueAmount.toFixed(2)}</td></tr>
  </table>

  <div class="divider"></div>

  <div style="font-size: 9px; margin-top: 4px;">
    <b>In Words (अक्षरेपी):</b><br/>
    <i>${amountInWords}</i>
  </div>
  ${checkoutNote ? `
  <div style="font-size: 9.5px; margin-top: 4px; padding: 2px 4px; border-left: 2px solid #000; word-break: break-word;">
    <b>Note / Remarks:</b> ${checkoutNote}
  </div>` : ""}

  <div class="divider"></div>

  <div class="footer">
    ${isEstimate ? `
      <div style="font-weight: bold; color: #b45309;">*** ESTIMATE BILL ONLY — THIS IS NOT A TAX INVOICE ***</div>
      <div>यो केवल अनुमानित बिल हो, यसलाई कर बिजक मानिने छैन । भुक्तानी पश्चात् आधिकारिक कर बिजक जारी गरिनेछ ।</div>
    ` : `
      <div>माल सामान तथा सेवा फिर्ता हुँदैन।</div>
      <div>THIS IS A COMPUTER GENERATED TAX INVOICE</div>
      <div style="margin-top: 3px; font-weight: bold;">Thank you for your visit! Please visit again.</div>
    `}
  </div>

  ${autoPrint ? `
  <script>
    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
  </script>
  ` : ""}
</body>
</html>`;
  }

  // -------------------------------------------------------------
  // FORMAT 2: MODERN COMPACT POS (80mm / 58mm)
  // -------------------------------------------------------------
  if (activeFormat === "modern_pos") {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate' : 'Receipt'} - ${invoiceNo}</title>
  <style>
    @page { size: 80mm auto; margin: 2mm 3mm; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      width: 72mm;
      margin: 0 auto;
      padding: 3mm 3mm;
      font-size: 11px;
      color: #111;
      line-height: 1.35;
    }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .badge { display: inline-block; padding: 2px 8px; font-size: 10px; font-weight: 800; background: #111; color: #fff; border-radius: 4px; letter-spacing: 0.5px; }
    .store-name { font-size: 16px; font-weight: 900; letter-spacing: -0.3px; margin: 2px 0; }
    .meta-box { background: #f4f4f5; border-radius: 6px; padding: 6px; margin: 6px 0; font-size: 10px; }
    .meta-box div { display: flex; justify-content: space-between; margin-bottom: 2px; }
    .divider { border-bottom: 1px solid #e4e4e7; margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 10px; }
    th { border-bottom: 2px solid #111; padding: 4px 0; text-align: left; font-size: 10px; text-transform: uppercase; }
    td { padding: 4px 0; border-bottom: 1px dashed #e4e4e7; vertical-align: top; }
    .total-box { background: #fafafa; border: 1px solid #e4e4e7; border-radius: 6px; padding: 6px; margin-top: 6px; }
    .total-box div { display: flex; justify-content: space-between; padding: 2px 0; font-size: 11px; }
    .grand-total { font-size: 13px !important; font-weight: 900; border-top: 1px solid #111; padding-top: 4px !important; margin-top: 3px; }
    .footer { font-size: 9px; color: #71717a; text-align: center; margin-top: 10px; }
  </style>
</head>
<body>
  <div class="text-center">
    <span class="badge" style="${isEstimate ? 'background: #d97706; color: #fff;' : ''}">
      ${isEstimate ? "ESTIMATE / PROFORMA (कर बिजक होइन)" : (isVat ? "TAX INVOICE" : "BILL OF SUPPLY")}
    </span>
    <div class="store-name">${hotelName}</div>
    <div style="font-size: 10px; color: #52525b;">${hotelAddress}</div>
    ${hotelPhone ? `<div style="font-size: 10px; color: #52525b;">Phone: ${hotelPhone}</div>` : ""}
    ${panNo ? `<div style="font-size: 11px; font-weight: 700; margin-top: 2px;">${isVat ? "VAT" : "PAN"}: ${panNo}</div>` : ""}
  </div>

  <div class="meta-box">
    <div><span>${isEstimate ? "Estimate Ref:" : "Invoice No:"}</span><strong>${invoiceNo}</strong></div>
    <div><span>Fiscal Year:</span><strong>${fiscalYear}</strong></div>
    <div><span>Date:</span><span>${transactionDate}</span></div>
    <div><span>Room:</span><strong>Room ${roomNumber} (${roomType})</strong></div>
    <div><span>Guest:</span><strong>${guestName}</strong></div>
    ${guestPan !== "N/A" ? `<div><span>Buyer PAN:</span><strong>${guestPan}</strong></div>` : ""}
  </div>

  <table>
    <thead>
      <tr>
        <th style="width: 55%;">Item</th>
        <th style="width: 15%; text-align: center;">Qty</th>
        <th style="width: 30%; text-align: right;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${items.map(it => `
        <tr>
          <td>
            <strong>${it.title}</strong><br/>
            <span style="font-size: 9px; color: #71717a;">${it.subtitle}</span>
          </td>
          <td style="text-align: center;">${it.qty}</td>
          <td style="text-align: right;"><strong>${it.total}</strong></td>
        </tr>
      `).join("")}
    </tbody>
  </table>

  <div class="total-box">
    <div><span>Subtotal:</span><span>${currency} ${subTotal.toFixed(2)}</span></div>
    ${discountAmt > 0 ? `<div><span>Discount:</span><span>- ${currency} ${discountAmt.toFixed(2)}</span></div>` : ""}
    ${serviceChargeAmt > 0 ? `<div><span>Service Charge:</span><span>+ ${currency} ${serviceChargeAmt.toFixed(2)}</span></div>` : ""}
    ${roundOffAmt !== 0 ? `<div><span>Round Off:</span><span>${roundOffAmt > 0 ? `+ ` : `- `}${currency} ${Math.abs(roundOffAmt).toFixed(2)}</span></div>` : ""}
    <div><span>Taxable Amount:</span><span>${currency} ${Number(taxableAmt).toFixed(2)}</span></div>
    ${vatAmt > 0 ? `<div><span>VAT (13%):</span><span>+ ${currency} ${Number(vatAmt).toFixed(2)}</span></div>` : ""}
    <div class="grand-total"><span>Total Payable:</span><span>${currency} ${Number(grandTotal).toFixed(2)}</span></div>
    ${advancePayment > 0 ? `<div><span>Advance Settled:</span><span>- ${currency} ${advancePayment.toFixed(2)}</span></div>` : ""}
    ${amountPaidNow > 0 ? `<div><span>Paid at Checkout (${paymentMethod}):</span><span>- ${currency} ${amountPaidNow.toFixed(2)}</span></div>` : ""}
    <div style="font-weight: 700;"><span>Total Paid:</span><span>${currency} ${totalPaid.toFixed(2)}</span></div>
    <div style="font-weight: 800; ${dueAmount > 0 ? 'color: #dc2626;' : 'color: #059669;'}"><span>Balance Due:</span><span>${currency} ${dueAmount.toFixed(2)}</span></div>
  </div>

  <div style="font-size: 9px; color: #52525b; margin-top: 6px; padding: 4px; background: #f4f4f5; border-radius: 4px;">
    <b>Amount In Words:</b> <i>${amountInWords}</i>
  </div>
  ${checkoutNote ? `
  <div style="font-size: 9.5px; color: #3f3f46; margin-top: 6px; padding: 4px 6px; background: #f4f4f5; border-left: 2px solid #18181b; border-radius: 4px; word-break: break-word;">
    <b>Note / Remarks:</b> ${checkoutNote}
  </div>` : ""}

  <div class="footer">
    <div>${isEstimate ? "PROFORMA FOLIO • NOT A TAX INVOICE (अनुमानित बिल)" : "Computer Generated Invoice • IRD Compliant"}</div>
    <div>Thank you for choosing ${hotelName}!</div>
  </div>

  ${autoPrint ? `
  <script>
    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
  </script>
  ` : ""}
</body>
</html>`;
  }

  // -------------------------------------------------------------
  // FORMAT 3: STANDARD IRD TAX INVOICE (A4 / A5 LETTERHEAD)
  // -------------------------------------------------------------
  if (activeFormat === "standard_a4_a5") {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate Folio' : 'Tax Invoice'} - ${invoiceNo}</title>
  <style>
    @page { size: A5 portrait; margin: 8mm; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
      margin: 0;
      padding: 0;
      font-size: 11px;
      color: #1f2937;
      line-height: 1.4;
    }
    .header-table { width: 100%; border-bottom: 2px solid ${isEstimate ? '#d97706' : '#059669'}; padding-bottom: 8mm; margin-bottom: 10px; }
    .store-title { font-size: 20px; font-weight: 900; color: ${isEstimate ? '#92400e' : '#065f46'}; margin: 0; }
    .tax-heading { text-align: right; }
    .tax-heading h2 { margin: 0; font-size: 18px; font-weight: 900; color: #111827; }
    .meta-container { display: flex; justify-content: space-between; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 12px; margin-bottom: 12px; font-size: 10.5px; }
    .meta-col { width: 48%; }
    .meta-col p { margin: 2px 0; }
    .meta-col strong { color: #111827; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .items-table th { background: #f3f4f6; color: #374151; font-weight: 800; text-transform: uppercase; font-size: 9.5px; padding: 6px 8px; border: 1px solid #d1d5db; }
    .items-table td { padding: 6px 8px; border: 1px solid #e5e7eb; font-size: 10px; vertical-align: top; }
    .calc-container { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; }
    .words-box { width: 55%; font-size: 10px; padding: 8px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; }
    .totals-table { width: 40%; border-collapse: collapse; font-size: 10.5px; }
    .totals-table td { padding: 3px 6px; border: none; }
    .totals-table .grand-row td { border-top: 2px solid #111827; border-bottom: 2px solid #111827; font-weight: 900; font-size: 12px; }
    .signature-grid { display: flex; justify-content: space-between; margin-top: 35px; padding-top: 10px; }
    .sig-block { width: 35%; text-align: center; border-top: 1px dashed #4b5563; padding-top: 4px; font-size: 10px; font-weight: bold; }
    .footer { text-align: center; border-top: 1px solid #e5e7eb; padding-top: 8px; margin-top: 15px; color: #6b7280; font-size: 9px; }
  </style>
</head>
<body>
  <table class="header-table">
    <tr>
      <td style="vertical-align: top;">
        <h1 class="store-title">${hotelName}</h1>
        <div style="font-size: 10px; color: #4b5563;">
          ${hotelAddress}${hotelPhone ? ` • Phone: ${hotelPhone}` : ""}${hotelEmail ? ` • Email: ${hotelEmail}` : ""}<br/>
          <strong>${isVat ? "VAT" : "PAN"} Registration No:</strong> ${panNo || "N/A"}
        </div>
      </td>
      <td class="tax-heading" style="vertical-align: top;">
        <h2 style="color: ${isEstimate ? '#b45309' : '#111827'};">
          ${isEstimate ? "PROFORMA INVOICE" : "TAX INVOICE"}
        </h2>
        <div style="font-size: 11px; font-weight: bold; color: ${isEstimate ? '#b45309' : '#065f46'};">
          ${isEstimate ? "अनुमानित बिल (यो कर बिजक होइन / NOT A TAX INVOICE)" : (isVat ? "कर बिजक (Under Nepal VAT Act, 2052)" : "संक्षिप्त कर बिजक")}
        </div>
        <div style="font-size: 10px; color: #6b7280; margin-top: 2px;">
          ${isEstimate ? "Estimate Ref:" : "Invoice No:"} <b>${invoiceNo}</b><br/>
          Fiscal Year (आ.व.): <b>${fiscalYear}</b>
        </div>
      </td>
    </tr>
  </table>

  ${isEstimate ? `
    <div style="background: #fffbeb; border: 1px dashed #f59e0b; border-radius: 6px; padding: 6px 10px; margin-bottom: 12px; text-align: center; color: #92400e; font-size: 11px; font-weight: bold;">
      ⚠️ THIS IS AN ESTIMATE BILL / PROFORMA FOLIO ONLY — NOT A FINAL TAX INVOICE (यो कर बिजक होइन)
    </div>
  ` : ""}

  <div class="meta-container">
    <div class="meta-col">
      <p><strong>Guest / Buyer Name:</strong> ${guestName}</p>
      <p><strong>Contact Phone:</strong> ${guestPhone}</p>
      <p><strong>Buyer PAN No (प्यान नं.):</strong> ${guestPan}</p>
      <p><strong>Date & Time:</strong> ${transactionDate}</p>
    </div>
    <div class="meta-col" style="text-align: right;">
      <p><strong>Room Assigned:</strong> Room ${roomNumber} (${roomType})</p>
      <p><strong>Stay Duration:</strong> ${nights} Night(s)</p>
      <p><strong>Check-in:</strong> ${checkInDate}</p>
      <p><strong>Check-out:</strong> ${checkOutDate}</p>
    </div>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th style="width: 6%; text-align: center;">SN</th>
        <th style="width: 50%; text-align: left;">Particulars / Description</th>
        <th style="width: 14%; text-align: center;">Qty / Days</th>
        <th style="width: 15%; text-align: right;">Rate (${currency})</th>
        <th style="width: 15%; text-align: right;">Amount (${currency})</th>
      </tr>
    </thead>
    <tbody>
      ${items.map(it => `
        <tr>
          <td style="text-align: center;">${it.sn}</td>
          <td>
            <strong>${it.title}</strong><br/>
            <span style="font-size: 9px; color: #6b7280;">${it.subtitle}</span>
          </td>
          <td style="text-align: center;">${it.qty}</td>
          <td style="text-align: right;">${it.rate}</td>
          <td style="text-align: right; font-weight: bold;">${it.total}</td>
        </tr>
      `).join("")}
    </tbody>
  </table>

  <div class="calc-container">
    <div class="words-box">
      <strong>Amount In Words (अक्षरेपी):</strong><br/>
      <span style="font-style: italic; color: #374151;">${amountInWords}</span><br/><br/>
      ${checkoutNote ? `<div style="margin-bottom: 6px; padding: 4px 6px; background: #fff; border-left: 3px solid #111827; font-size: 10px; word-break: break-word;"><strong>Note / Remarks:</strong> ${checkoutNote}</div>` : ''}
      <span style="font-size: 9px; color: #6b7280;">
        ${isEstimate ? 'Provisional charges for verification prior to check-out settlement.' : 'Tax invoice generated in full compliance with Nepal Inland Revenue Department rules.'}
      </span>
    </div>

    <table class="totals-table">
      <tr><td>Gross Subtotal:</td><td style="text-align: right;">${currency} ${subTotal.toFixed(2)}</td></tr>
      ${discountAmt > 0 ? `<tr><td>Discount:</td><td style="text-align: right;">- ${currency} ${discountAmt.toFixed(2)}</td></tr>` : ""}
      ${serviceChargeAmt > 0 ? `<tr><td>Service Charge:</td><td style="text-align: right;">+ ${currency} ${serviceChargeAmt.toFixed(2)}</td></tr>` : ""}
      ${roundOffAmt !== 0 ? `<tr><td>Round Off:</td><td style="text-align: right;">${roundOffAmt > 0 ? `+ ` : `- `}${currency} ${Math.abs(roundOffAmt).toFixed(2)}</td></tr>` : ""}
      <tr><td>Taxable Amount:</td><td style="text-align: right;">${currency} ${Number(taxableAmt).toFixed(2)}</td></tr>
      ${vatAmt > 0 ? `<tr><td>13% VAT Amount:</td><td style="text-align: right;">+ ${currency} ${Number(vatAmt).toFixed(2)}</td></tr>` : ""}
      <tr class="grand-row"><td>Grand Total:</td><td style="text-align: right;">${currency} ${Number(grandTotal).toFixed(2)}</td></tr>
      ${advancePayment > 0 ? `<tr><td>Advance Received:</td><td style="text-align: right;">- ${currency} ${advancePayment.toFixed(2)}</td></tr>` : ""}
      ${amountPaidNow > 0 ? `<tr><td>Paid at Checkout (${paymentMethod}):</td><td style="text-align: right;">- ${currency} ${amountPaidNow.toFixed(2)}</td></tr>` : ""}
      <tr style="font-weight: bold;"><td>Total Paid:</td><td style="text-align: right;">${currency} ${totalPaid.toFixed(2)}</td></tr>
      <tr style="font-weight: bold; ${dueAmount > 0 ? 'color: #b91c1c;' : 'color: #047857;'} font-size: 11px;"><td>Net Due at Checkout:</td><td style="text-align: right;">${currency} ${dueAmount.toFixed(2)}</td></tr>
    </table>
  </div>

  <div class="signature-grid">
    <div class="sig-block">Guest / Receiver Signature</div>
    <div class="sig-block">Prepared & Authorized By<br/><span style="font-size: 8.5px; font-weight: normal;">${hotelName}</span></div>
  </div>

  <div class="footer">
    ${isEstimate ? `
      <div style="color: #b45309; font-weight: bold;">*** ESTIMATE ONLY — NOT A TAX INVOICE ***</div>
      <div>Provisional billing presented for checkout review. Official Tax Invoice will be generated upon full settlement.</div>
    ` : `
      <div>Certified that the particulars given above are true and correct. Goods/Services once sold are non-refundable.</div>
      <div>Thank you for choosing ${hotelName}!</div>
    `}
  </div>

  ${autoPrint ? `
  <script>
    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
  </script>
  ` : ""}
</body>
</html>`;
  }

  // -------------------------------------------------------------
  // FORMAT 4: EXECUTIVE HOTEL & FINE DINING FOLIO (A4 / A5 LUXURY)
  // -------------------------------------------------------------
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Guest Proforma' : 'Guest Folio'} - ${invoiceNo}</title>
  <style>
    @page { size: A5 portrait; margin: 8mm; }
    body {
      font-family: 'Garamond', 'Georgia', serif, -apple-system, sans-serif;
      margin: 0;
      padding: 0;
      font-size: 11px;
      color: #1c1917;
      line-height: 1.4;
    }
    .folio-header { border-bottom: 3px double ${isEstimate ? '#d97706' : '#d97706'}; padding-bottom: 8px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: flex-end; }
    .resort-name { font-size: 22px; font-weight: 900; color: ${isEstimate ? '#92400e' : '#78350f'}; text-transform: uppercase; letter-spacing: 1px; margin: 0; }
    .folio-title { text-align: right; }
    .folio-title h2 { font-size: 17px; font-weight: 900; color: ${isEstimate ? '#b45309' : '#78350f'}; margin: 0; letter-spacing: 1px; }
    .guest-card { display: flex; justify-content: space-between; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 10px 14px; margin-bottom: 14px; font-size: 11px; }
    .guest-card p { margin: 2px 0; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    th { background: ${isEstimate ? '#92400e' : '#78350f'}; color: #fff; font-weight: bold; text-transform: uppercase; font-size: 9px; letter-spacing: 0.8px; padding: 6px 8px; }
    td { padding: 6px 8px; border-bottom: 1px solid #f5f5f4; font-size: 10.5px; }
    .totals-area { display: flex; justify-content: space-between; margin-top: 8px; }
    .words-card { width: 50%; font-size: 10px; border-left: 3px solid #d97706; padding-left: 10px; }
    .totals-box { width: 45%; font-size: 11px; }
    .totals-box tr td { padding: 3px 0; }
    .grand-total-row { font-size: 13px; font-weight: 900; color: ${isEstimate ? '#b45309' : '#78350f'}; border-top: 2px solid ${isEstimate ? '#b45309' : '#78350f'}; border-bottom: 2px solid ${isEstimate ? '#b45309' : '#78350f'}; }
    .sign-section { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 10px; font-size: 10px; }
    .sign-line { width: 35%; text-align: center; border-top: 1px solid #78350f; padding-top: 4px; font-weight: bold; }
    .footer { text-align: center; font-size: 9px; color: #78716c; margin-top: 15px; border-top: 1px solid #e7e5e4; padding-top: 6px; }
  </style>
</head>
<body>
  <div class="folio-header">
    <div>
      <h1 class="resort-name">${hotelName}</h1>
      <div style="font-size: 10px; color: #57534e;">
        ${hotelAddress}${hotelPhone ? ` | Tel: ${hotelPhone}` : ""}<br/>
        <strong>${isVat ? "VAT" : "PAN"} Registration:</strong> ${panNo || "N/A"}
      </div>
    </div>
    <div class="folio-title">
      <h2>${isEstimate ? "PROFORMA FOLIO" : "EXECUTIVE FOLIO"}</h2>
      <div style="font-size: 10px; color: ${isEstimate ? '#b45309' : '#78350f'}; font-weight: bold;">
        ${isEstimate ? "ESTIMATE BILL / अनुमानित बिल (कर बिजक होइन)" : "कर बिजक / TAX INVOICE"}
      </div>
      <div style="font-size: 10px; color: #78716c;">Folio ID: #${booking?.id || "000"} | ${isEstimate ? "Est Ref" : "Inv"}: ${invoiceNo}</div>
    </div>
  </div>

  ${isEstimate ? `
    <div style="background: #fef3c7; border-left: 4px solid #d97706; padding: 6px 12px; margin-bottom: 12px; font-size: 10.5px; color: #92400e; font-weight: bold;">
      PROFORMA ESTIMATE: Presented for guest review prior to checkout settlement. Official IRD Tax Invoice issued upon checkout payment.
    </div>
  ` : ""}

  <div class="guest-card">
    <div>
      <p><strong>Distinguished Guest:</strong> ${guestName}</p>
      <p><strong>Contact:</strong> ${guestPhone}</p>
      <p><strong>Guest PAN:</strong> ${guestPan}</p>
      <p><strong>Fiscal Period:</strong> ${fiscalYear}</p>
    </div>
    <div style="text-align: right;">
      <p><strong>Room Assigned:</strong> Suite / Room ${roomNumber} (${roomType})</p>
      <p><strong>Nights Reserved:</strong> ${nights} Night(s)</p>
      <p><strong>Arrival:</strong> ${checkInDate}</p>
      <p><strong>Departure:</strong> ${checkOutDate}</p>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width: 8%; text-align: center;">#</th>
        <th style="width: 52%; text-align: left;">Description of Charges</th>
        <th style="width: 12%; text-align: center;">Qty</th>
        <th style="width: 14%; text-align: right;">Rate (${currency})</th>
        <th style="width: 14%; text-align: right;">Total (${currency})</th>
      </tr>
    </thead>
    <tbody>
      ${items.map(it => `
        <tr>
          <td style="text-align: center; color: #78716c;">${it.sn}</td>
          <td>
            <strong>${it.title}</strong><br/>
            <span style="font-size: 9px; color: #78716c;">${it.subtitle}</span>
          </td>
          <td style="text-align: center;">${it.qty}</td>
          <td style="text-align: right;">${it.rate}</td>
          <td style="text-align: right; font-weight: bold;">${it.total}</td>
        </tr>
      `).join("")}
    </tbody>
  </table>

  <div class="totals-area">
    <div class="words-card">
      <strong>Amount In Words (अक्षरेपी):</strong><br/>
      <i style="color: #44403c;">${amountInWords}</i><br/><br/>
      ${checkoutNote ? `<div style="margin-bottom: 6px; padding: 4px 6px; background: #fff; border-left: 3px solid #78350f; font-size: 10px; word-break: break-word;"><strong>Note / Remarks:</strong> ${checkoutNote}</div>` : ''}
      <span style="font-size: 9px; color: #78716c;">
        ${isEstimate ? 'Provisional breakdown subject to final settlement during checkout.' : 'All food, accommodation, and hospitality services billed subject to Nepal IRD Tax Invoice regulations.'}
      </span>
    </div>

    <table class="totals-box">
      <tr><td>Folio Subtotal:</td><td style="text-align: right;">${currency} ${subTotal.toFixed(2)}</td></tr>
      ${discountAmt > 0 ? `<tr><td>Discount:</td><td style="text-align: right;">- ${currency} ${discountAmt.toFixed(2)}</td></tr>` : ""}
      ${serviceChargeAmt > 0 ? `<tr><td>Service Charge:</td><td style="text-align: right;">+ ${currency} ${serviceChargeAmt.toFixed(2)}</td></tr>` : ""}
      ${roundOffAmt !== 0 ? `<tr><td>Round Off:</td><td style="text-align: right;">${roundOffAmt > 0 ? `+ ` : `- `}${currency} ${Math.abs(roundOffAmt).toFixed(2)}</td></tr>` : ""}
      <tr><td>Taxable Services:</td><td style="text-align: right;">${currency} ${Number(taxableAmt).toFixed(2)}</td></tr>
      ${vatAmt > 0 ? `<tr><td>13% Value Added Tax:</td><td style="text-align: right;">+ ${currency} ${Number(vatAmt).toFixed(2)}</td></tr>` : ""}
      <tr class="grand-total-row"><td>Total Charges:</td><td style="text-align: right;">${currency} ${Number(grandTotal).toFixed(2)}</td></tr>
      ${advancePayment > 0 ? `<tr><td>Advance Settled:</td><td style="text-align: right;">- ${currency} ${advancePayment.toFixed(2)}</td></tr>` : ""}
      ${amountPaidNow > 0 ? `<tr><td>Paid at Checkout (${paymentMethod}):</td><td style="text-align: right;">- ${currency} ${amountPaidNow.toFixed(2)}</td></tr>` : ""}
      <tr style="font-weight: bold;"><td>Total Settled:</td><td style="text-align: right;">${currency} ${totalPaid.toFixed(2)}</td></tr>
      <tr style="font-weight: 900; ${dueAmount > 0 ? 'color: #991b1b;' : 'color: #047857;'} font-size: 12px;"><td>Balance Due:</td><td style="text-align: right;">${currency} ${dueAmount.toFixed(2)}</td></tr>
    </table>
  </div>

  <div class="sign-section">
    <div class="sign-line">Guest Signature</div>
    <div class="sign-line">Front Desk Executive<br/><span style="font-size: 8px; font-weight: normal;">${hotelName}</span></div>
  </div>

  <div class="footer">
    ${isEstimate ? `
      <div>*** THIS IS A PROFORMA ESTIMATE FOLIO — NOT A TAX INVOICE ***</div>
    ` : `
      <div>Thank you for staying with us. We hope you enjoyed your hospitality experience.</div>
    `}
  </div>

  ${autoPrint ? `
  <script>
    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 600); };
  </script>
  ` : ""}
</body>
</html>`;
}
